import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Carousel from '../components/Carousel';
import Icon from '../components/Icon';
import { Avatar, Page, Petals, Reveal, SkeletonGrid } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { firstWord, greeting } from '../utils/format';

const LINES = [
  'Someone special might be one interest away.',
  'Your matches were busy while you were on duty.',
  'Fresh faces, curated just for you today.',
  'Love finds a way — even between shifts.',
];

function CompletionRing({ value, children }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <div className="cring">
      <svg viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} className="cring__track" />
        <motion.circle
          cx="60"
          cy="60"
          r={r}
          className="cring__bar"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c * (1 - value / 100) }}
          transition={{ duration: 1.4, ease: 'easeOut', delay: 0.3 }}
        />
      </svg>
      <div className="cring__inner">{children}</div>
      <span className="cring__pct">{value}%</span>
    </div>
  );
}

function QuickLink({ to, icon, label, hint, tint, count, i }) {
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 30, scale: 0.9 }, show: { opacity: 1, y: 0, scale: 1 } }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <Link to={to} className={`qlink tint-${tint}`}>
        <motion.span
          className="qlink__blob"
          animate={{ y: [0, -5, 0], rotate: [0, i % 2 ? 4 : -4, 0] }}
          transition={{ duration: 4 + (i % 4) * 0.6, repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }}
        >
          <Icon name={icon} size={26} />
          {count > 0 && <span className="qlink__count">{count}</span>}
        </motion.span>
        <span className="qlink__text">
          <strong>{label}</strong>
          <small>{hint}</small>
        </span>
        <span className="qlink__arrow"><Icon name="arrowRight" size={16} /></span>
      </Link>
    </motion.div>
  );
}

export default function Home() {
  const { user, counts, isPremium } = useApp();
  const [data, setData] = useState(null);
  const [visitors, setVisitors] = useState(0);
  const line = LINES[new Date().getDate() % LINES.length];
  // "Arjun, someone special …": lower-case the line's first letter after the name.
  const words = line.split(' ');
  const lead = words.slice(0, 2).join(' ').replace(/^./, (c) => c.toLowerCase());
  const rest = words.slice(2).join(' ');

  useEffect(() => {
    api('/profiles/discover').then(setData).catch(() => setData({ daily: [] }));
    api('/profiles/visitors').then((d) => setVisitors(d.total)).catch(() => {});
  }, []);

  const links = [
    { to: '/matches', icon: 'heart', label: 'Daily matches', hint: `${data?.daily?.length ?? '…'} picked for you`, tint: 'rose' },
    { to: '/search', icon: 'search', label: 'Search', hint: 'Filter by city, specialty…', tint: 'violet' },
    { to: '/interests', icon: 'inbox', label: 'Interests', hint: counts.interests ? `${counts.interests} waiting for you` : 'Received & sent', tint: 'gold', count: counts.interests },
    { to: '/messages', icon: 'chat', label: 'Chats', hint: counts.chats ? `${counts.chats} unread` : 'Your conversations', tint: 'teal', count: counts.chats },
    { to: '/visitors', icon: 'eye', label: 'Who viewed me', hint: `${visitors} recent visitors`, tint: 'violet' },
    { to: '/shortlist', icon: 'star', label: 'Shortlisted', hint: `${user.shortlist?.length || 0} saved profiles`, tint: 'gold' },
    { to: '/kundli', icon: 'moon', label: 'Kundli match', hint: 'Guna milan in seconds', tint: 'rose' },
    { to: '/me/preferences', icon: 'sliders', label: 'Preferences', hint: 'Tune who you see', tint: 'teal' },
  ];

  return (
    <Page className="home">
      <section className="lounge-hero">
        <Petals count={9} />
        <div className="container lounge-hero__inner">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 160 }}>
            <CompletionRing value={user.profileCompletion || 0}>
              <Avatar src={user.photos?.[0]} name={user.name} size={92} />
            </CompletionRing>
          </motion.div>
          <div className="lounge-hero__copy">
            <motion.span className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{greeting()}</motion.span>
            <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              {firstWord(user.firstName)}, <em>{lead}</em> {rest}
            </motion.h1>
            <motion.div className="lounge-hero__pills" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <span className="pill pill--soft">ID {user.profileId}</span>
              {isPremium ? <span className="pill pill--gold"><Icon name="crown" size={13} /> {user.premium.plan.replace(/^./, (c) => c.toUpperCase())} member</span> : <Link to="/premium" className="pill pill--gold shine"><Icon name="crown" size={13} /> Go Premium</Link>}
              {user.source === 'medhire' && <span className="pill pill--teal"><Icon name="steth" size={13} /> MedHire linked</span>}
              {user.profileCompletion < 100 && (
                <Link to="/me/edit" className="pill pill--rose">Profile {user.profileCompletion}% · complete it <Icon name="arrowRight" size={12} /></Link>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <div className="container">
        <section className="destinations">
          <Reveal className="destinations__head">
            <h2>Where would you like to go?</h2>
          </Reveal>
          <motion.div className="qlinks" initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.2 } } }}>
            {links.map((l, i) => <QuickLink key={l.to} {...l} i={i} />)}
          </motion.div>
        </section>

        {!data ? (
          <SkeletonGrid count={4} />
        ) : (
          <>
            <Carousel title="Today's picks for you" sub="Hand-picked using your partner preferences" items={data.daily} to="/matches" />
            {!isPremium && (
              <Reveal className="promo">
                <div className="promo__art">
                  <motion.span animate={{ rotate: [0, 10, -6, 0] }} transition={{ repeat: Infinity, duration: 4 }}><Icon name="crown" size={40} /></motion.span>
                </div>
                <div>
                  <h3>Talk to matches directly</h3>
                  <p className="muted">Premium members get 4× more responses, unlimited chats and verified contact numbers.</p>
                </div>
                <Link to="/premium" className="btn btn--gold shine">See plans <Icon name="arrowRight" size={16} /></Link>
              </Reveal>
            )}
            <Carousel title="Fresh on CTshaadi" sub="Joined recently — be the first to say hello" items={data.newest} compact to="/matches?tab=newest" />
            <Carousel title={`Near you in ${user.city || 'your city'}`} items={data.nearby} compact to="/matches?tab=nearby" />
            <Carousel title="From your profession" sub={`Fellow ${user.profession?.toLowerCase() || 'professional'}s who get your schedule`} items={data.sameProfession} compact to="/matches?tab=sameProfession" />
            {user.source === 'medhire' && (
              <Reveal className="promo promo--teal">
                <div className="promo__art"><Icon name="refresh" size={36} /></div>
                <div>
                  <h3>Updated your MedHire profile?</h3>
                  <p className="muted">Pull your latest education, job and salary from MedHire and pick what to update here.</p>
                </div>
                <Link to="/me/edit?sync=1" className="btn btn--teal">Sync from MedHire</Link>
              </Reveal>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
